// =====================================================================
// Catalog filter store — single source of truth for active filters on
// the catalog page. Persists across navigation within the SPA, resets
// explicitly. Selectors are stable references (zustand recomputes when
// the underlying array changes).
// =====================================================================

import { create } from "zustand";
import { ZERO_UUID_SENTINEL } from "@/shared/lib/db-schema";
import { z } from "zod";

export type CatalogSort = "newest" | "price_asc" | "price_desc";

interface CatalogFiltersState {
  search: string;
  gender: string;
  category: string;
  brand: string;
  color: string;
  size: string;
  sort: CatalogSort;
  setSearch: (v: string) => void;
  setGender: (v: string) => void;
  setCategory: (v: string) => void;
  setBrand: (v: string) => void;
  setColor: (v: string) => void;
  setSize: (v: string) => void;
  setSort: (v: CatalogSort) => void;
  reset: () => void;
}

const initial = {
  search: "",
  gender: ZERO_UUID_SENTINEL,
  category: ZERO_UUID_SENTINEL,
  brand: ZERO_UUID_SENTINEL,
  color: ZERO_UUID_SENTINEL,
  size: ZERO_UUID_SENTINEL,
  sort: z.enum(["newest", "price_asc", "price_desc"]).parse("newest"),
};

export const useFiltersStore = create<CatalogFiltersState>()((set) => ({
  ...initial,
  setSearch: (search) => set({ search }),
  setGender: (gender) => set({ gender }),
  setCategory: (category) => set({ category }),
  setBrand: (brand) => set({ brand }),
  setColor: (color) => set({ color }),
  setSize: (size) => set({ size }),
  setSort: (sort) => set({ sort }),
  reset: () => set({ ...initial }),
}));

export const useFilters = () => useFiltersStore((s) => s);
export const useFilterSearch = () => useFiltersStore((s) => s.search);
export const useFilterGender = () => useFiltersStore((s) => s.gender);
export const useFilterCategory = () => useFiltersStore((s) => s.category);
export const useFilterBrand = () => useFiltersStore((s) => s.brand);
export const useFilterColor = () => useFiltersStore((s) => s.color);
export const useFilterSize = () => useFiltersStore((s) => s.size);
export const useFilterSort = () => useFiltersStore((s) => s.sort);
